import type { TaskExecutionSummary, TaskView } from '@/lib/api/generated/model';
import { runOutcome } from '@/features/tasks';

/**
 * One line of the run's activity timeline.
 *
 * `outcome` is set only on a finished run, in the execution history's own
 * words and colour (`runOutcome`), so a run reads the same here as in the
 * task's Executions tab.
 */
export interface RunActivityEvent {
  id: string;
  at: string;
  kind: 'fan-out-started' | 'task-created' | 'entered-step' | 'run-finished';
  taskId: string | null;
  taskTitle: string | null;
  stepName: string | null;
  outcome: { label: string; colourClass: string } | null;
  /** A failed or blocked run's own message. */
  detail: string | null;
}

/**
 * Statuses a finished run ends in, worth a line of their own. `skipped` is
 * absent on purpose: it is the synthetic run recorded when a task reaches the
 * done column, already said by that task's "entered Done" line.
 */
const FINISHED = new Set<TaskExecutionSummary['status']>([
  'completed',
  'failed',
  'blocked',
  'stopped',
]);

/**
 * The run's history as one feed, newest first: the fan-out run starting,
 * each created task appearing, and each of those tasks entering a step and
 * finishing a run there.
 *
 * Read entirely from data the page already holds. A move always records an
 * execution of the step it lands on (the rule `stepEntryTime` relies on), so
 * an execution's `created_at` is when its task entered that step; no move
 * history is fetched. Consequently a move that has not happened yet, or one
 * the service did not record, is simply absent — nothing here is inferred.
 *
 * `stepLabels` names steps by id on the *target* board; an id it lacks (a step
 * the workflow no longer declares) is shown as the id itself.
 */
export function runActivity(
  execution: TaskExecutionSummary,
  tasks: TaskView[],
  stepLabels: Map<string, string>,
): RunActivityEvent[] {
  const events: RunActivityEvent[] = [
    {
      id: `fan-out:${execution.id}`,
      at: execution.started_at ?? execution.created_at,
      kind: 'fan-out-started',
      taskId: null,
      taskTitle: null,
      stepName: null,
      outcome: null,
      detail: null,
    },
  ];

  for (const task of tasks) {
    events.push({
      id: `created:${task.id}`,
      at: task.created_at,
      kind: 'task-created',
      taskId: task.id,
      taskTitle: task.title,
      stepName: null,
      outcome: null,
      detail: null,
    });

    for (const run of task.executions ?? []) {
      const stepName =
        stepLabels.get(run.step_id_snapshot) ?? run.step_id_snapshot;
      events.push({
        id: `entered:${run.id}`,
        at: run.created_at,
        kind: 'entered-step',
        taskId: task.id,
        taskTitle: task.title,
        stepName,
        outcome: null,
        detail: null,
      });
      if (FINISHED.has(run.status) && run.ended_at) {
        events.push({
          id: `finished:${run.id}`,
          at: run.ended_at,
          kind: 'run-finished',
          taskId: task.id,
          taskTitle: task.title,
          stepName,
          outcome: runOutcome(run.status),
          detail:
            run.status === 'failed' || run.status === 'blocked'
              ? (run.error_message?.trim() ?? null) || null
              : null,
        });
      }
    }
  }

  // Newest first; a tie keeps a stable, meaningful order — a run finishing
  // after its task entered the step, a task existing before it moved.
  const rank: Record<RunActivityEvent['kind'], number> = {
    'run-finished': 0,
    'entered-step': 1,
    'task-created': 2,
    'fan-out-started': 3,
  };
  return events.sort((a, b) =>
    a.at !== b.at ? (a.at < b.at ? 1 : -1) : rank[a.kind] - rank[b.kind],
  );
}
